import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  acceptAll,
  getConsent,
  hasDecided,
  OPEN_SETTINGS_EVENT,
  rejectAll,
  setConsent,
} from '@/lib/cookieConsent';

export default function CookieConsentBanner() {
  const [bannerVisible, setBannerVisible] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [functional, setFunctional] = useState(true);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  // Initial mount: show banner if no decision yet
  useEffect(() => {
    if (!hasDecided()) {
      setBannerVisible(true);
    } else {
      const existing = getConsent();
      if (existing) {
        setFunctional(existing.functional);
        setAnalytics(existing.analytics);
        setMarketing(existing.marketing);
      }
    }
  }, []);

  // Listen for global "open settings" requests (footer / account menu)
  useEffect(() => {
    const handler = () => {
      const existing = getConsent();
      if (existing) {
        setFunctional(existing.functional);
        setAnalytics(existing.analytics);
        setMarketing(existing.marketing);
      }
      setSettingsOpen(true);
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, handler);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, handler);
  }, []);

  const handleAcceptAll = () => {
    acceptAll();
    setFunctional(true);
    setAnalytics(true);
    setMarketing(true);
    setBannerVisible(false);
    setSettingsOpen(false);
  };

  const handleRejectAll = () => {
    rejectAll();
    setFunctional(false);
    setAnalytics(false);
    setMarketing(false);
    setBannerVisible(false);
    setSettingsOpen(false);
  };

  const handleSavePreferences = () => {
    setConsent({ functional, analytics, marketing });
    setBannerVisible(false);
    setSettingsOpen(false);
  };

  const openSettings = () => setSettingsOpen(true);

  return (
    <>
      {bannerVisible && (
        <div
          role="dialog"
          aria-label="Cookie consent"
          className="fixed bottom-0 left-0 right-0 z-[9000] border-t bg-background shadow-lg"
          style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
          <div className="container mx-auto px-4 py-3 md:py-4 flex flex-col gap-3 md:flex-row md:items-center md:gap-4">

            <div className="flex items-start gap-3 flex-1 min-w-0">
              <Cookie className="h-5 w-5 text-primary mt-0.5 shrink-0" />
              <p className="text-sm text-muted-foreground leading-snug">
                We use cookies to enhance your experience, analyze usage, and assist with marketing.
                By clicking "Accept All," you agree to our use of cookies. You can manage preferences anytime.{' '}
                <Link to="/cookies" className="text-primary underline whitespace-nowrap">
                  View Cookie Policy
                </Link>
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 shrink-0">
              <Button variant="outline" size="sm" onClick={handleRejectAll}>
                Reject All
              </Button>
              <Button variant="outline" size="sm" onClick={openSettings}>
                Cookie Settings
              </Button>
              <Button size="sm" onClick={handleAcceptAll}>
                Accept All
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cookie className="h-5 w-5 text-primary" />
              Cookie Preferences
            </DialogTitle>
            <DialogDescription>
              Choose which categories of cookies LaneRunner can use. You can change this anytime.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <PreferenceRow
              title="Essential"
              description="Required for login, payments, and core features. Always on."
              checked={true}
              disabled
            />
            <PreferenceRow
              title="Functional"
              description="Save preferences and improve usability."
              checked={functional}
              onChange={setFunctional}
            />
            <PreferenceRow
              title="Analytics"
              description="Help us understand usage and improve the platform."
              checked={analytics}
              onChange={setAnalytics}
            />
            <PreferenceRow
              title="Marketing"
              description="Track referrals and campaign performance."
              checked={marketing}
              onChange={setMarketing}
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={handleRejectAll}>
              Reject All
            </Button>
            <Button variant="outline" onClick={handleAcceptAll}>
              Accept All
            </Button>
            <Button onClick={handleSavePreferences}>Save Preferences</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface PreferenceRowProps {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (next: boolean) => void;
}

function PreferenceRow({ title, description, checked, disabled, onChange }: PreferenceRowProps) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-md border p-3">
      <div className="min-w-0">
        <div className="text-sm font-semibold text-foreground">{title}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{description}</div>
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={(v) => onChange?.(!!v)}
      />
    </div>
  );
}
